'use client';

import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Pill } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import type {
  CamelCaseParallelDealer,
  CamelCaseSubmodel,
} from '@/apis/server/new-car/v1/car-detail';
import type {
  CamelCasePromotionList,
} from '@/apis/server/new-car/v1/promotions';
import type {
  CamelCasePromotion,
} from '@/apis/server/new-car/v1/promotions/types';
import {
  PromotionDetailsModal,
} from '@/app/_components/page_content/new-car-promo/promotion-details-modal';
import {
  TITLE_HEADING_H2,
  TITLE_HEADING_H3,
} from '@/components/section-container/base';
import { SectionContainer } from '@/components/section-container/page';
import { AUTHORISED_DISTRIBUTORS } from '@/constants/global';
import { createTimestampUid } from '@/utils/id';
import { joinSgcmMedia } from '@/utils/url/join/public';

import {
  SECTION_TITLE_RECOMMENDED_FOR_YOU,
} from '../../../_components/page-content/constants';
import { EnquiryForm } from '../../../_components/page-content/enquiry-form';
import {
  PiReadyStockModal,
} from '../../../_components/page-content/pi-ready-stock-modal';
import {
  RecommendedForYouForInfo,
} from '../../../_components/page-content/recommended-for-you-for-info';
import {
  SponsoredContent,
} from '../../../_components/page-content/sponsored-content';
import {
  getCompetitorPromotions,
} from '../../../_components/page-content/utils';
import {
  NO_PRICE_HISTORY,
  SECTION_TITLE_DEALER_DETAIL,
  SECTION_TITLE_PRICE_HISTORY,
  SECTION_TITLE_SUBMODEL_INFO,
} from './constants';
import { DealerDetail } from './dealer-detail';
import { LineChart } from './line-chart';
import type {
  ChartData,
  ChartDataMonthMap,
  CoePriceChartMonth,
} from './line-chart/types';
import { ParallelDealers } from './parallel-dealers';
import { PiPromoSwiper } from './pi-promo-swiper';
import { PriceHistory } from './price-history';
import styles from './styles.module.scss';
import { SubModelModal } from './sub-model-modal';
import { SubModels } from './sub-models';
import type { Component } from './types';

export const PageContent: Component = ({
  dealerDetailDataList,
  carDetailData,
  modelId,
  promotionList,
  coeRecentTrendList,
}) => {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en', { month: 'short' });
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const carPromo = (promotionList ?? []).reduce((acc, item) => {
    const matchingPromo = item.models
      .some(model => model.id === Number(modelId));

    if (matchingPromo) {
      return [...acc, item];
    }

    return acc;
  }, [] as CamelCasePromotionList);

  const [
    promotionDetailsModalKey,
    setPromotionDetailsModalKey,
  ] = useState(createTimestampUid);

  const [
    isPromotionDetailsModalOpen,
    setIsPromotionDetailsModalOpen,
  ] = useState(false);

  const [
    activePromotionListPromoId,
    setActivePromotionListPromoId,
  ] = useState<number | null>(null);

  const activePromotionDetailsItem = promotionList
    ? promotionList.find(promotion => promotion.models.some(
      promoModel => promoModel.id === activePromotionListPromoId,
    )) as CamelCasePromotion
    : null;

  const [chartTab, setChartTab] = useState('6-Months');
  const [cartMonth, setChartMonth] = useState<CoePriceChartMonth>(6);

  const handleLineChartTabOnClick = (month: CoePriceChartMonth) => () => {
    const newTab = `${month}-Months`;

    setChartTab(newTab);

    setChartMonth(month);
    window.location.hash = `#${newTab}`;
  };

  const availableSubModels = carDetailData.subModels.filter(
    item => item.isAvailable === true,
  );

  const categoryList = availableSubModels.map(model => ({
    title: model.name,
    description: model.transmission,
  }));

  function mergeData(history: { price: number; date: string; }[][]):
  (string | number | null)[][] {
    const allDates = new Set(history.flat().map(item => item.date));

    const groupByDate = (data: { price: number; date: string; }[]):
    Record<string, number[]> => data.reduce<
      Record<string, number[]>>((acc, { price, date }) => ({
      ...acc,
      [date]: [...(acc[date] || []), price],
    }), {});
    const groupedData: Record<string, number[]>[] = history.map(groupByDate);

    const result: (string | number | null)[][] = [];

    allDates.forEach((date) => {
      const prices = groupedData.map(group => (
        group[date] ? group[date][0] : null));

      result.push([date, ...prices]);
    });

    result.sort((a, b) => (a[0] as string).localeCompare(b[0] as string));

    return result;
  }

  const [isOpen, setIsOpen] = useState(false);

  const [subModel, setSubModel] = useState<
  CamelCaseSubmodel | undefined>(undefined);

  const handleSubModelModalOnClick = (id: number) => {
    const subModelData = availableSubModels.find(model => model.id === id);

    setSubModel(subModelData);
    setIsOpen(true);
  };

  const subModelsHistory = availableSubModels.map(item => item.history);
  const mergedData = mergeData(subModelsHistory);

  const formatDateForPriceHistory = (dateString: string): string => {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);

    return `${day}-${month}-${year}`;
  };

  const getFilteredDataByMonths = (months: number): ChartData[] => {
    const today = new Date();
    const pastDate = new Date(today);

    pastDate.setMonth(today.getMonth() - months);

    return mergedData.filter((item) => {
      const itemDateStr = item[0];
      if (typeof itemDateStr !== 'string') return false;
      const itemDate = new Date(itemDateStr);
      return itemDate >= pastDate && itemDate <= today;
    }).map((item) => {
      const itemDateStr = item[0];

      const formattedDate = (typeof itemDateStr === 'string' && itemDateStr)
        ? formatDateForPriceHistory(itemDateStr)
        : null;
      return [formattedDate, ...item.slice(1)] as ChartData;
    });
  };

  const sixMonthData: ChartData[] = getFilteredDataByMonths(6);
  const twelveMonthData: ChartData[] = getFilteredDataByMonths(12);
  const twentyFourMonthData: ChartData[] = getFilteredDataByMonths(24);

  const mergedDataArray: ChartDataMonthMap = {
    6: sixMonthData,
    12: twelveMonthData,
    24: twentyFourMonthData,
  };

  const [isPiPopupOpen, setIsPiPopupOpen] = useState(false);

  const [readyStocksDealerData, setReadyStocksDealerData] = useState<
  CamelCaseParallelDealer[] | null>(null);

  const onOpenReadyStocks = (id: number) => {
    setIsPiPopupOpen(true);
    const dealerData = carDetailData?.parallelDealers?.filter(
      dealer => dealer.id === id,
    );

    setReadyStocksDealerData(dealerData ?? null);
  };

  const [noPriceHistory, setNoPriceHistory] = useState(false);

  const isDataEqual = (
    data1: ChartData[],
    data2: ChartData[],
  ) => JSON.stringify(data1) === JSON.stringify(data2);

  const shouldShowPills = () => {
    if (isDataEqual(sixMonthData, twelveMonthData)
      && isDataEqual(sixMonthData, twentyFourMonthData)) {
      return [6]; // Only show pill 6
    }

    if (isDataEqual(twelveMonthData, twentyFourMonthData)) {
      return [6, 12]; // Show pill 6 and pill 12
    }
    return [6, 12, 24]; // Show all pills
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');

      if (['6-Months', '12-Months', '24-Months'].includes(hash)) {
        setChartTab(hash);
        setChartMonth(parseInt(hash, 10) as CoePriceChartMonth);
      }
    }
  }, []);

  useEffect(() => {
    if (sixMonthData.length === 0
      && twelveMonthData.length === 0
      && twentyFourMonthData.length === 0) {
      setNoPriceHistory(true);
    }
  }, [sixMonthData, twelveMonthData, twentyFourMonthData]);

  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();

  const competitorPromotions = getCompetitorPromotions(
    promotionList,
    carDetailData.id,
  );

  const { isSpotlight } = carDetailData.additionalProperties;

  return (
    <main
      className={styles.containerPageRoot}>
      { carDetailData.dealerType === AUTHORISED_DISTRIBUTORS
        ? (
          <>
            <SectionContainer
              className={styles.containerPageContent}
              title={SECTION_TITLE_PRICE_HISTORY}
              titleHeading={TITLE_HEADING_H2}>
              <div className={styles.containerTabAndPrice}>
                <div className={styles.containerTabs}>
                  { noPriceHistory
                    ? null
                    : (
                      <div className={styles.containerTabs}>
                        {shouldShowPills().map(months => (
                          <Pill
                            key={months}
                            activeBackgroundColor="red-1"
                            activeTextColor="red-1"
                            activeTextWeight="semibold"
                            backgroundColor="gray-1"
                            className={cx(styles.pill, styles.isSmall)}
                            hoverTextColor="red-1"
                            isActive={chartTab === `${months}-Months`}
                            textColor="gray-1"
                            onClick={handleLineChartTabOnClick(months as
                            CoePriceChartMonth)}>
                            {months}
                            {' '}
                            Months
                          </Pill>
                        ))}
                      </div>
                    ) }
                </div>
                <div className={styles.containerPriceHistory}>
                  { noPriceHistory
                    ? (
                      <div className={styles.noPriceHistoryContainer}>
                        <Image
                          alt="No Price History "
                          height={144}
                          src={`${joinSgcmMedia('/public/new-cars/no-search-results.svg')}`}
                          width={164} />
                        <div className={styles.textNoPrice}>
                          {NO_PRICE_HISTORY}
                        </div>
                      </div>
                    )
                    : (
                      <LineChart
                        categoryList={categoryList}
                        data={mergedDataArray}
                        month={cartMonth} />
                    ) }
                  <PriceHistory
                    carDetailData={carDetailData}
                    dealerDetailDataList={dealerDetailDataList} />
                </div>
              </div>
            </SectionContainer>
            {
              isDesktopMode && competitorPromotions && (
                <SectionContainer
                  title=""
                  titleHeading="div">
                  <SponsoredContent
                    modelId={Number(modelId)}
                    promotions={promotionList} />
                </SectionContainer>
              )
            }
            <SectionContainer
              className={styles.containerPageContent}
              title={SECTION_TITLE_SUBMODEL_INFO}
              titleAsideContent={(
                <div className={styles.textUpdated}>
                  Date Updated
                  {' '}
                  {carDetailData.authorizeDealers
                  && formatDate(carDetailData.authorizeDealers[0]?.updatedAt)}
                </div>
              )}
              titleHeading={TITLE_HEADING_H3}>
              <SubModels
                carDetailData={carDetailData}
                onOpenSubModelModal={handleSubModelModalOnClick} />
              <SubModelModal
                carDetailData={carDetailData}
                coeRecentTrendList={coeRecentTrendList}
                isOpen={isOpen}
                subModelData={subModel}
                onClose={() => setIsOpen(false)} />
            </SectionContainer>
            <SectionContainer
              className={styles.containerPageContent}
              title={SECTION_TITLE_DEALER_DETAIL}
              titleAsideContent={(
                <button
                  className={styles.btnAddToAlert}
                  onClick={() => {
                    document.getElementById('shortlistIcon')?.click();
                  }}>
                  Add to Alert
                </button>
              )}
              titleHeading={TITLE_HEADING_H3}>
              <DealerDetail dealerDetailDataList={dealerDetailDataList} />
            </SectionContainer>
          </>
        )
        : (
          <>
            <EnquiryForm
              carId={Number(modelId)}
              dealers={carDetailData.parallelDealers || []}
              isSpotlight={isSpotlight}
              make={carDetailData.make}
              model={carDetailData.model}
              promotions={promotionList} />
            <SectionContainer
              className={styles.containerPageContent}
              title=""
              titleHeading={TITLE_HEADING_H3}>
              <div className={styles.containerDistributor}>
                <ParallelDealers
                  carDetailData={carDetailData}
                  carPromo={carPromo}
                  onOpenReadyStocks={onOpenReadyStocks} />
                <PiReadyStockModal
                  dealerId={null}
                  fullName={`${carDetailData.make} ${carDetailData.model}`}
                  isOpen={isPiPopupOpen}
                  modelId={Number(modelId)}
                  modelThumbnail={carDetailData.image.thumbnailUrl}
                  piDealerListData={readyStocksDealerData}
                  submodelList={carDetailData.subModels}
                  onClose={() => setIsPiPopupOpen(false)} />
                { carPromo.length > 0 && (
                  <PiPromoSwiper
                    carPromo={carPromo}
                    onItemClick={(promoId) => {
                      setActivePromotionListPromoId(promoId);
                      setIsPromotionDetailsModalOpen(true);
                    }} />
                )}
              </div>
            </SectionContainer>
            <PromotionDetailsModal
              key={promotionDetailsModalKey}
              isOpen={isPromotionDetailsModalOpen}
              promotion={activePromotionDetailsItem}
              onClose={() => {
                setIsPromotionDetailsModalOpen(false);
                window.history.pushState(null, '', ' ');
              }}
              onExit={() => {
                setPromotionDetailsModalKey(createTimestampUid());
              }} />
          </>
        ) }
      <SectionContainer
        className={styles.containerPageContent}
        title={SECTION_TITLE_RECOMMENDED_FOR_YOU}
        titleHeading={TITLE_HEADING_H2}>
        <RecommendedForYouForInfo />
      </SectionContainer>
    </main>
  );
};
